import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Modal,
  Paragraph,
  Tag,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCameraExposureFilled } from '@zillow/constellation-icons';
import { useRentalManagerUserPropertySpotlightWebApiHandler_remove } from '@zg-rentals/ts-rental-manager-api-client-react-query';

type Props = {
  isOpen: boolean;
  listingAlias: string;
  listingName: string;
  availabilityText: string;
  onClose: () => void;
  onSuccess: () => void;
};

const SpotlightRemovalModal = ({ isOpen, listingAlias, listingName, availabilityText, onClose, onSuccess }: Props) => {
  const { enqueueToast } = useToast();
  const { mutateAsync: removeSpotlight, isPending } = useRentalManagerUserPropertySpotlightWebApiHandler_remove({
    onSuccess: () => {
      enqueueToast(<Toast tone="success">Listing Spotlight successfully removed from {listingName}.</Toast>);
      onSuccess();
    },
    onError: () => {
      enqueueToast(<Toast tone="critical">Listing Spotlight couldn’t be removed. Try again.</Toast>);
      return;
    },
  });
  const handleRemove = async () => {
    await removeSpotlight({ getParams: { listingAlias } });
  };
  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', md: 'md' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Remove Listing Spotlight
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', alignItems: 'flex-start' }}>
                <Tag size="md" tone="yellow" icon={<IconCameraExposureFilled />}>
                  {availabilityText}
                </Tag>
                <Paragraph>
                  After removing the Listing Spotlight from {listingName}, you can reassign it to another property that
                  needs more visibility.
                </Paragraph>
                <Alert tone="info" css={{ alignSelf: 'stretch' }}>
                  Allow up to 24 hours for Listing Spotlight updates to apply across all platforms
                </Alert>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Actions">
                <TextButton tone="brand" onClick={onClose}>
                  Close
                </TextButton>
                <Button
                  tone="brand"
                  emphasis="filled"
                  size="md"
                  loading={isPending}
                  disabled={isPending}
                  onClick={handleRemove}
                >
                  Remove
                </Button>
              </ButtonGroup>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default SpotlightRemovalModal;
